// /login-skolon (DEV-677) — pure helpers, kept out of the page so they can
// be unit-tested without loading auth0-js.

export type SkolonScreen =
  | 'notConfigured'
  | 'noLicence'
  | 'preparing'
  | 'noSeats'
  | 'ended'
  | 'error'
  // After logging out. A terminal screen on purpose: see
  // SKOLON_LOGOUT_RETURN_PATH.
  | 'loggedOut';

// The Auth0 fetchUserProfile script rejects users with no valid Skolon
// licence for our tool with this message. It reaches the callback as the
// hash's `error_description` (auth0-js exposes it as `errorDescription`).
// No Auth0 user is created in that case.
export const SKOLON_NO_LICENCE = 'skolon_no_licence';

// The Skolon tool is listed in the Swedish market only, so an arrival with no
// locale cookie most likely reads Swedish. Only used for the copy on this
// page; the account's real locale comes back from activation.
export const SKOLON_FALLBACK_LOCALE = 'sv-SE';

// Where getRedirect sends a Skolon user instead of /voucher. `from=app` tells
// /login-skolon to activate with the token it already has instead of
// starting SSO again, and every result there other than an active
// subscription is a terminal screen — so / → here can't loop.
// How fresh Auth0 must consider the session, in seconds, sent as `max_age` on
// the Skolon authorize call. ⚠️ This is the SAME number as jakari's
// SKOLON_CLAIM_MAX_AGE_SECONDS, not a smaller one: jakari accepts up to
// SKOLON_CLAIM_MAX_AGE_SECONDS + SKOLON_CLAIM_CLOCK_SKEW_SECONDS (600 + 60),
// and that skew allowance is jakari's business — a second margin here would
// only re-authenticate people more often than needed. `auth_time` appears in
// the id_token BECAUSE we ask for max_age, so a window longer than jakari's
// would 401 every activation.
export const SKOLON_DEFAULT_MAX_AGE_SECONDS = 600;

export const getSkolonMaxAgeSeconds = (raw?: string | number): number => {
  const parsed = Number(raw);
  // Unset, blank, non-numeric, zero or negative → the default. A zero would
  // force a re-authentication on every single arrival.
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return SKOLON_DEFAULT_MAX_AGE_SECONDS;
  }
  return Math.floor(parsed);
};

export const SKOLON_LOGIN_PATH = '/login-skolon';
export const SKOLON_APP_RETURN_PATH = `${SKOLON_LOGIN_PATH}?from=app`;

// Where a logged-out Skolon user lands. They have no credential of ours —
// no voucher, no passphrase, no school to pick — so /school and
// /login-roster are both dead ends for them.
//
// `from=logout` makes this page show a screen and WAIT. It must not start
// SSO on arrival: their Skolon session usually outlives ours, so they would
// be signed straight back in and logout would appear not to work.
//
// ⚠️ Scope of that protection, stated precisely: it stops a SILENT
// re-login, not re-login. AuthApi.logout (pages/logout.tsx) calls Auth0
// without `federated`, so only our session is cleared and Skolon's stays
// live — one tap of the button still returns the previous user. On a shared
// classroom device the next pupil must sign out of Skolon itself. Making
// the logout federated is the real fix; see DEV-677 follow-ups.
export const SKOLON_LOGOUT_RETURN_PATH = `${SKOLON_LOGIN_PATH}?from=logout`;

// PREFERRED_LOGIN_VARIANT's value for a Skolon account, written once
// activation succeeds and read by getRedirect in _app. Mirrors
// login-staff's 'staff'.
export const SKOLON_LOGIN_VARIANT = 'skolon';

// What the "sign in another way" link resets the variant to — _app's own
// default, so getRedirect then routes by locale exactly as it would for a
// browser that had never seen Skolon (en-GB → /school, otherwise the
// passphrase form). Resetting and re-entering `/` is deliberately NOT the
// same as linking straight to a login page: the routing rules live in
// getRedirect, and duplicating them here would be a second copy to keep in
// step.
export const DEFAULT_LOGIN_VARIANT = 'phone';

// Skolon's tool listing can carry "deep links" — tiles that open a named
// section rather than the app's front page. They cannot point straight at
// toosa: an unauthenticated arrival there bounces to peikko, which has no
// way to know it came from Skolon and routes by leftover cookies, landing
// the user on /login-roster or the passphrase form — a login they cannot
// use. So a deep link points HERE, and names its destination in `next`.
//
// The value is a toosa path, not a peikko one. It reaches toosa through the
// `returnPath` cookie, which toosa's middleware already reads on the login
// callback — the same mechanism that makes /school?name=settle survive an
// auth round trip. peikko and toosa share the registrable domain, so the
// cookie crosses.
export const SKOLON_NEXT_PARAM = 'next';

// Measured on the percent-encoded value, which is what actually goes into
// the cookie. Long enough for any real section path, short enough that it cannot
// crowd out the JWT and base64 user cookies on the shared domain — browsers
// silently drop a cookie over ~4 KB, which would degrade the deep link to `/`
// with no error anywhere.
export const SKOLON_NEXT_MAX_LENGTH = 512;

// Same-origin but never a legitimate deep-link target. `/logout` would wedge
// the user in a login loop on every later sign-in, and `/login` is toosa's own
// token callback.
const FORBIDDEN_FIRST_SEGMENTS = new Set(['login', 'logout']);

// Any origin works; it exists only so the relative path can be parsed and
// normalised. `.invalid` is reserved by RFC 2606 and can never resolve.
const NEXT_PATH_BASE = 'https://skolon-next.invalid';

// A same-origin path and nothing else. Rejects absolute URLs, scheme-relative
// `//evil.com` (which a browser treats as absolute), and anything not starting
// with a single slash — so this cannot become an open redirect.
//
// ⚠️ This is the ONLY real guard. toosa's isValidReturnUrl validates the
// DECODED form but redirects with the raw one, and has neither the
// control-character nor the backslash check below — so it cannot be relied
// on as a second line. See the toosa follow-up.
export const getSkolonNextPath = (search: string): string | null => {
  const raw = new URLSearchParams(search || '').get(SKOLON_NEXT_PARAM);
  if (!raw) return null;
  // A browser STRIPS tab, LF and CR while parsing a URL, so `/<tab>/evil.example`
  // survives a naive startsWith('/') check and then resolves as
  // `//evil.example` — scheme-relative, and therefore off-site. toosa's own
  // isValidReturnUrl has the same gap, so this is the only thing standing in
  // the way. Reject control characters outright rather than enumerate the
  // three that happen to matter today.
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001F\u007F]/.test(raw)) return null;
  if (!raw.startsWith('/') || raw.startsWith('//')) return null;
  // A backslash is normalised to a forward slash by some browsers, so
  // `/\evil.com` would escape the origin check above.
  if (raw.includes('\\')) return null;
  if (raw.length > SKOLON_NEXT_MAX_LENGTH) return null;

  // Everything above inspects the RAW string, which is not what a browser
  // eventually resolves. `/./logout` and `/a/../logout` both normalise to
  // `/logout`, so a first-segment check on the raw text is trivially
  // sidestepped. Parse it the way the browser will, against a placeholder
  // origin, and judge the result.
  let parsed: URL;
  try {
    parsed = new URL(raw, NEXT_PATH_BASE);
  } catch (e) {
    return null;
  }
  // If anything in `raw` escaped to another origin, this is where it shows.
  if (parsed.origin !== NEXT_PATH_BASE) return null;

  // ...but the origin check alone is not enough, because normalisation can
  // manufacture a scheme-relative PATH that still parses under the base:
  // `/..//evil.example` has pathname `//evil.example` here, and resolving
  // THAT in a browser leaves the site. The raw `//` test above cannot see it,
  // since the raw string starts `/.`. Re-apply the check to what we return.
  if (!parsed.pathname.startsWith('/') || parsed.pathname.startsWith('//')) {
    return null;
  }

  const firstSegment = (parsed.pathname.split('/')[1] || '').toLowerCase();
  if (FORBIDDEN_FIRST_SEGMENTS.has(firstSegment)) return null;

  // A bare path only — no query, no fragment. A deep link names a section,
  // and nothing more is needed. Allowing a nested query hands the next page
  // an attacker-chosen parameter, and some of toosa's pages feed one
  // straight to router.push: `/grade-group-selection?from=…` is a navigation
  // sink, and an encoded newline inside that value slips past toosa's own
  // `javascript:` substring test. Refusing the whole shape removes the class
  // rather than the instance.
  if (parsed.search || parsed.hash) return null;

  const path = parsed.pathname;

  // Cap the SERIALISED size, not the character count: nookies percent-encodes
  // the value, so 511 multi-byte characters become ~4,600 bytes and the
  // browser silently drops the cookie — losing the destination, or worse,
  // leaving an older one in place.
  if (encodeURIComponent(path).length > SKOLON_NEXT_MAX_LENGTH) return null;

  // The NORMALISED path, not the raw one, so that what was validated here is
  // exactly what toosa receives — no second parse, no differential.
  return path;
};

export const isSkolonNoLicenceError = (err: unknown): boolean => {
  if (!err || typeof err !== 'object') return false;
  const { error, errorDescription, error_description, description } =
    err as Record<string, unknown>;
  return [error, errorDescription, error_description, description].some(
    (value) => typeof value === 'string' && value.includes(SKOLON_NO_LICENCE),
  );
};

// jakari's machine-readable error codes (the `code` field; `error` is a
// generic message). st-jakari#372 / st-identify#934.
//
// A stale claim is the one failure that can NEVER be retried with the same
// credential. jakari requires `auth_time` (its `iat` fallback is gone as of
// bb7e591) and a silent checkSession() renewal does not move `auth_time`, so
// only a real re-authentication — prompt=login, or a fresh trip through the
// Skolon tile — can satisfy the gate.
export const SKOLON_CLAIM_STALE = 'skolon_claim_stale';
// The token isn't from the Skolon connection at all — never retryable.
export const SKOLON_CLAIM_UNTRUSTED = 'skolon_claim_untrusted';

// jakari POST /user/skolon/activate failure → which screen to show. jakari
// answers `{ error, code, timestamp }`, where `code` is the machine-readable
// one (st-jakari#372 / st-identify#934); the status alone is enough to pick a
// screen, so we branch on that and leave `code` for logs.
//   401 skolon_claim_stale             — NOT a screen: restart SSO (below)
//   401 (anything else)                — renewed once by IdentifyApi, then
//                                        a generic error
//   403 skolon_claim_missing           — generic error
//   403 skolon_claim_untrusted         — generic error, never retried
//   404 skolon_customer_not_configured  — licensed in Skolon, no vouchers yet
//   409 skolon_seats_exhausted          — the customer's vouchers are full
//   410 skolon_access_ended             — the contract/voucher has expired
//   422 skolon_user_type_unsupported    — e.g. a Skolon administrator
//   502                                 — jakari upstream/timeout
//   anything else                       — generic error
export const getSkolonActivationFailureScreen = (
  status?: number,
): SkolonScreen => {
  if (status === 404 || status === 422) return 'preparing';
  if (status === 409) return 'noSeats';
  if (status === 410) return 'ended';
  return 'error';
};

// The callback leg: Auth0 put either a token or an error in the fragment.
export const isAuth0CallbackHash = (hash: string): boolean =>
  /(^|[#&])(id_token|access_token|error)=/.test(hash || '');

export type SkolonEntryAction =
  | 'parseCallback'
  | 'activateFromApp'
  | 'startSignIn'
  | 'showLoggedOut'
  // Already signed in: go straight to the app, no SSO (see below).
  | 'handOff';

// What /login-skolon does on arrival:
//   - Auth0 callback in the hash → parse it (then activate)
//   - `from=logout` → show the signed-out screen and wait for a click
//   - `from=app` with a token cookie → activate straight away, no SSO
//   - any other arrival WITH a token → hand off, no SSO at all
//   - otherwise → start SSO
//
// That third case is what makes a deep link usable twice. Previously any
// arrival without `from` started SSO even when the user was already signed
// in with an active subscription, which is both a pointless round trip and
// an actively broken one: jakari requires a fresh `auth_time` on the Skolon
// claim, so re-activating with a reused session is rejected as stale.
//
// ⚠️ This DOES weaken one check, and the trade should be explicit. The old
// path sent `max_age=600` on every authorize, which forced Auth0 to
// re-authenticate — and so re-run Skolon's fetchUserProfile, the licence
// gate — whenever the last authentication was over ten minutes old. Handing
// off skips that, so a revoked Skolon licence keeps working from the tile
// until the JWT stops being accepted.
//
// How long that is: NOT the cookie lifetimes (peikko 24h, toosa 7 days) —
// those are retention, and the id_token inside expires in ~2h (README
// "Token & renewal model"; toosa verifies it in its own middleware). But
// Skolon is a redirect-based social connection, so it DOES leave an Auth0
// SSO session, which means IdentifyApi's 401 retry can renew silently via
// checkSession — and `prompt=none` re-authenticates nobody, so it does not
// re-run fetchUserProfile either. Treat the window as lasting until that SSO
// session ends rather than ~2h.
//
// What does NOT change: an already-open session was never re-checked either,
// and jakari's validateVoucher still gates every request — but it gates the
// SUBSCRIPTION, not the Skolon licence, so it does not close this.
//
// Accepted because the alternative is worse in practice: re-authenticating
// on every tile click is exactly what jakari rejects as a stale claim, which
// is the bug this replaces. Revisit if licence revocation needs to bite
// faster than token expiry.
//
// The callback check stays first as a defensive measure, not because a
// callback is expected to carry a query: AuthApi's redirectUri is
// `/login-skolon` with no query string, so in practice the hash arrives on
// a bare URL. Parsing a token is what completes a sign-in, so it should win
// over any query we might add later.
export const getSkolonEntryAction = ({
  hash,
  search,
  token,
}: {
  hash: string;
  search: string;
  token?: string | null;
}): SkolonEntryAction => {
  if (isAuth0CallbackHash(hash)) return 'parseCallback';
  const from = new URLSearchParams(search || '').get('from');
  if (from === 'logout') return 'showLoggedOut';
  if (from === 'app' && token) return 'activateFromApp';
  if (token) return 'handOff';
  return 'startSignIn';
};

export type SkolonActivationOutcome =
  | { kind: 'handoff'; user: User }
  // The claim was too old: re-authorize with prompt=login, don't renew.
  | { kind: 'restartSignIn'; status?: number; code?: string }
  | {
      kind: 'screen';
      screen: SkolonScreen;
      status?: number;
      code?: string;
      error?: unknown;
    };

// Runs the activation call and decides the page's next step. Only an ACTIVE
// subscription hands off to `/`.
//
// An ended contract arrives as a 410 (above), not as a 200 with an inactive
// status — identify picks a usable voucher or fails. The `status !== active`
// branch is a defensive fallback to the same terminal screen: it must never
// hand off to `/`, because getRedirect would bounce an inactive user
// straight back here.
export const resolveSkolonActivation = async (
  activate: () => Promise<User>,
): Promise<SkolonActivationOutcome> => {
  let user: User;
  try {
    user = await activate();
  } catch (err) {
    if (err?.status === 401 && err?.code === SKOLON_CLAIM_STALE) {
      return { kind: 'restartSignIn', status: 401, code: err.code };
    }
    return {
      kind: 'screen',
      screen: getSkolonActivationFailureScreen(err?.status),
      status: err?.status,
      code: err?.code,
      error: err,
    };
  }
  if (user?.status !== 'active') {
    return { kind: 'screen', screen: 'ended', status: 200 };
  }
  return { kind: 'handoff', user };
};
